import { ScrollTop } from 'primereact/scrolltop';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { APP_LOAD, LOGOUT, REDIRECT } from '../constants/actionTypes';
import StatusMonitoring from './StatusMonitoring';
import Navbar from './Header/Navbar';
import Terminals from './Terminals';
import agent from '../agent';
import Login from '../components/Login';
import Register from '../components/Register';
import ResetPassword from '../components/ResetPassword';
import PostalService from './Admin/PostalService';
import PostalRoute from './Admin/PostalRoute';
import ProductCategory from './Admin/ProductCategory';
import Orders from './Order';
import Order from './Order/Order';
import Calculator from './Calculator';
import CalculatorSplit from './CalculatorSplit';
import UserSettings from './User';
import SLQUser from './Admin/SLQUser';
import SLQUsers from './Admin/SLQUsers';
import CalcTariffs from './Admin/CalcTariffs';
import CalcTariff from './Admin/CalcTariff';
import CrossDockingTariffs from "./Admin/CrossDockingTariffs";
import CrossDockingTariffDetails from "./Admin/CrossDockingTariffs/Details";
import SlqTariffs from './Admin/SlqTariffs';

// styles (PrimeReact v10+: theme + icons; keep PrimeFlex if you use it)
import 'primeicons/primeicons.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primeflex/primeflex.css';
import SLQPostalService from "./Admin/SLQPostalService";
import Dashboard from "./Admin/Dashboard";
import Dpd from "./Admin/Dpd";
import Users from "./Admin/Users";
import AuditLogs from "./Admin/AuditLogs";
import Forbidden403 from "./Forbidden403";
import {AuthProvider} from "./AuthContext";
import ProtectedRoute from "./ProtectedRoute";
import Catalog from "./Catalog";
import SessionProvider from "./SessionProvider/SessionProvider";
import LogisticsHome from "./Home/LogisticsHome";

const CrossDockingTariffsLegacyRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/cross-docking-tarrifs/${id}`} replace />;
};

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { appLoaded, appName, currentUser, redirectTo } = useSelector(
      (state) => state.common
  );

  useEffect(() => {
    const token = window.localStorage.getItem('sid');
    agent.setToken(token);

    dispatch({
      type: APP_LOAD,
      payload: token ? agent.Auth.current() : null,
      token,
      skipTracking: true,
    });
  }, [dispatch]);

  useEffect(() => {
    if (!appLoaded) {
      return;
    }

    if (redirectTo && redirectTo !== location.pathname) {
      navigate(redirectTo, { replace: true });
    }
    if (redirectTo) {
      dispatch({ type: REDIRECT });
    }
  }, [appLoaded, redirectTo, location.pathname, navigate, dispatch]);

  const onClickLogout = useCallback(() => {
    dispatch({ type: LOGOUT });
  }, [dispatch]);

  const onStayAliveRequest = useCallback(async () => {
    try {
      await agent.Auth.current();
    } catch (e) {
      // Session check failures are handled by existing auth flow.
    }
  }, []);

  return (
      <SessionProvider
        enabled={Boolean(currentUser)}
        onLogout={onClickLogout}
        onStayAliveRequest={onStayAliveRequest}
      >
        <div>
          <Navbar appName={appName} currentUser={currentUser} logoutHandler={onClickLogout} />
          <div id="page-wrapper">
            {appLoaded && (
                <AuthProvider>
                    <Routes>
                    <Route path="/" element={<LogisticsHome />} />
                    <Route path="/login" element={currentUser ? <Navigate to="/" replace /> : <Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    <Route path="/admin/*" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "SUPERUSER"]}>
                            <Navigate to="/" replace />
                        </ProtectedRoute>
                    } />


                    <Route path="/postal_services" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <PostalService />
                        </ProtectedRoute>
                    } />


                    <Route path="/slq_postal_services" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "SUPERUSER"]}>
                            <SLQPostalService />
                        </ProtectedRoute>
                    } />
                    <Route path="/postal_routes" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <PostalRoute />
                        </ProtectedRoute>
                        } />
                    <Route path="/product_categories" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <ProductCategory />
                        </ProtectedRoute>
                        } />

                    <Route path="/orders" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <Orders />
                        </ProtectedRoute>
                        } />

                    <Route path="/order/:orderNum" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                        <Order />
                        </ProtectedRoute>
                    } />

                    <Route path="/calculator" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <Calculator />
                        </ProtectedRoute>
                        } />
                    <Route path="/calculator_shoulders" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <CalculatorSplit />
                        </ProtectedRoute>
                        } />
                    <Route path="/settings" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER", "ACCOUNTANT"]}>
                            <UserSettings />
                        </ProtectedRoute>
                    } />

                    <Route path="/users" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER", "ACCOUNTANT"]}>
                            <SLQUsers />
                        </ProtectedRoute>
                    } />
                    <Route path="/terminals" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <Terminals />
                        </ProtectedRoute>
                    } />

                    <Route path="/user/:guid" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER", "ACCOUNTANT"]}>
                            <SLQUser />
                        </ProtectedRoute>
                    } />

                    <Route path="/calc-tariffs" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <CalcTariffs />
                        </ProtectedRoute>
                    } />

                    <Route path="/cross-docking-tarrifs" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <CrossDockingTariffs />
                        </ProtectedRoute>
                    } />

                    <Route path="/cross-docking-tarrifs/:id" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <CrossDockingTariffDetails />
                        </ProtectedRoute>
                    } />

                    <Route path="/cross-docking-tariffs" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <Navigate to="/cross-docking-tarrifs" replace />
                        </ProtectedRoute>
                    } />

                    <Route path="/cross-docking-tariffs/:id" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <CrossDockingTariffsLegacyRedirect />
                        </ProtectedRoute>
                    } />


                    <Route path="/calc-tariff/:tid" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <CalcTariff />
                        </ProtectedRoute>
                    } />

                    <Route path="/slq-tariffs" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "SUPERUSER"]}>
                            <SlqTariffs />
                        </ProtectedRoute>
                    } />

                    <Route path="/status_monitoring" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <StatusMonitoring />
                        </ProtectedRoute>
                    } />

                    <Route path="/panel" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "SUPERUSER"]}>
                            <Dashboard/>
                        </ProtectedRoute>
                    } />
                    <Route path="/panel/dpd" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <Dpd/>
                        </ProtectedRoute>
                    } />
                    <Route path="/panel/users" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <Users/>
                        </ProtectedRoute>
                    } />
                    <Route path="/panel/audit-logs" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                            <AuditLogs/>
                        </ProtectedRoute>
                    } />

                    <Route path="/status_monitoring" element={
                      <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                        <StatusMonitoring />
                      </ProtectedRoute>
                    } />

                    <Route path="/catalog" element={
                      <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                        <Catalog/>
                      </ProtectedRoute>
                    } />

                    <Route path="/billing">
                      <Route path="accounting-catalog" element={
                        <ProtectedRoute allowedRoles={["ADMIN", "OWNER", "LOGIST", "SUPERUSER"]}>
                          <Catalog type="billing"/>
                        </ProtectedRoute>
                      }/>
                    </Route>

                    <Route path="/403" element={<Forbidden403/>} />
                    </Routes>
                </AuthProvider>
            )}
            <ScrollTop />
          </div>
        </div>
      </SessionProvider>
  );
}

export default App;
